import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';

import { agentRanges, buildQueue } from '../../../lib/agentSpans.js';
import { readSelection } from '../../../lib/offsets.js';
import { humanAddedSpans } from '../../../lib/remap.js';
import { SPAN_TYPES, TAG_META, findOverlap } from '../../../lib/spans.js';
import { useEditorStore } from '../../../store/editorStore.js';
import { useUiStore } from '../../../store/uiStore.js';
import { Keycap } from '../../common/index.jsx';
import { CaptionSurface } from '../CaptionSurface.jsx';
import { FinalPreview } from '../FinalPreview.jsx';
import { SelectionPopover } from '../SelectionPopover.jsx';
import { SpanPopover } from '../SpanPopover.jsx';
import {
  AdjudicationStats,
  DecisionLegend,
  DecisionList,
  DecisionQueue,
} from './DecisionQueue.jsx';
import { SpanGrid } from './SpanGrid.jsx';
import { shortSource } from './AnnotationToolbar.jsx';

/**
 * The admin annotation workspace - the old Final Annotation tab, rebuilt.
 *
 * Same sections in the same order: adjudication summary, the tag buttons, the active
 * decision panel, the plain agent caption, the original-vs-final comparison, the full
 * decision lists, the final saved caption, and the per-tag span grid.
 */
export function AdminWorkspace({ sample, draft, slot, onClear }) {
  const surfaceRef = useRef(null);
  const [minimized, setMinimized] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const selection = useEditorStore((s) => s.selection);
  const setSelection = useEditorStore((s) => s.setSelection);
  const spanCursor = useEditorStore((s) => s.spanCursor);
  const cursorScroll = useEditorStore((s) => s.cursorScroll);
  const setCursor = useEditorStore((s) => s.setCursor);
  const addTag = useEditorStore((s) => s.addTag);
  const updateSpan = useEditorStore((s) => s.updateSpan);
  const retagSpan = useEditorStore((s) => s.retagSpan);
  const removeSpan = useEditorStore((s) => s.removeSpan);
  const restoreSpan = useEditorStore((s) => s.restoreSpan);
  const commitCaptionEdit = useEditorStore((s) => s.commitCaptionEdit);
  const undoCaptionEdit = useEditorStore((s) => s.undoCaptionEdit);
  const dismissRemapReport = useEditorStore((s) => s.dismissRemapReport);
  const setFinalCaption = useEditorStore((s) => s.setFinalCaption);
  const setDecision = useEditorStore((s) => s.setDecision);
  const setDetailDecision = useEditorStore((s) => s.setDetailDecision);
  const setDecisionNote = useEditorStore((s) => s.setDecisionNote);
  const spanPopover = useEditorStore((s) => s.spanPopover);
  const openSpanPopover = useEditorStore((s) => s.openSpanPopover);
  const closeSpanPopover = useEditorStore((s) => s.closeSpanPopover);

  const showTagLetters = useUiStore((s) => s.showTagLetters);
  const toast = useUiStore((s) => s.toast);

  /* --- the decision queue ------------------------------------------------------ */

  const queue = useMemo(
    () =>
      buildQueue(
        sample?.final_spans || [],
        sample?.missing_details || [],
        draft.working,
      ),
    [sample, draft.working],
  );

  const decisions = useMemo(
    () => Object.fromEntries(draft.spanDecisions.map((d) => [d.span_index, d])),
    [draft.spanDecisions],
  );
  const detailDecisions = useMemo(
    () => Object.fromEntries(draft.detailDecisions.map((d) => [d.detail_index, d])),
    [draft.detailDecisions],
  );

  const activeIndex = Math.max(
    0,
    queue.findIndex((q) => q.id === activeId),
  );
  const activeItem = queue[activeIndex];

  // On load, start at the first undecided item - the old tab's behaviour.
  useEffect(() => {
    if (!queue.length) return;
    const firstPending = queue.find((q) => {
      const map = q.kind === 'span' ? decisions : detailDecisions;
      return !map[q.index]?.decision;
    });
    setActiveId((firstPending || queue[0]).id);
    // Only when the caption changes, not on every decision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.sampleId, queue.length]);

  const moveQueue = useCallback(
    (step) => {
      if (!queue.length) return;
      const next = (activeIndex + step + queue.length) % queue.length;
      setActiveId(queue[next].id);
    },
    [queue, activeIndex],
  );

  const decide = useCallback(
    (verdict, item = activeItem, advance = true) => {
      if (!item) return;
      if (item.kind === 'span') setDecision(slot, item.index, verdict);
      else if (verdict !== 'wrong_label_or_action') setDetailDecision(slot, item.index, verdict);
      else return;
      if (advance) moveQueue(1);
    },
    [activeItem, slot, setDecision, setDetailDecision, moveQueue],
  );

  /* --- keyboard, scoped to this workspace -------------------------------------- */

  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target?.tagName?.toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || event.target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const map = {
        ArrowUp: () => decide('confirmed'),
        ArrowDown: () => decide('rejected'),
        x: () => activeItem?.kind === 'span' && decide('wrong_label_or_action'),
        n: () => moveQueue(1),
        p: () => moveQueue(-1),
        m: () => setMinimized((v) => !v),
      };
      const run = map[event.key] || map[event.key.toLowerCase()];
      if (!run) return;
      event.preventDefault();
      run();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [decide, moveQueue, activeItem]);

  /* --- highlight layers --------------------------------------------------------- */

  const decisionRanges = useMemo(
    () => agentRanges(queue, decisions, activeId, detailDecisions),
    [queue, decisions, activeId, detailDecisions],
  );

  // The final caption is a different string from the working caption, so the agent's
  // spans have to be re-located against it rather than reusing these offsets. Pass the
  // inputs and let FinalPreview do that against the text it owns.
  const agentLayer = useMemo(
    () => ({
      finalSpans: sample?.final_spans || [],
      missingDetails: sample?.missing_details || [],
      decisions,
      detailDecisions,
    }),
    [sample, decisions, detailDecisions],
  );

  const captureSelection = useCallback(
    (event) => {

      setSelection(readSelection(surfaceRef.current));
    },
    [setSelection],
  );

  useEffect(() => {
    if (spanCursor < 0 || !cursorScroll) return;
    const node = surfaceRef.current?.querySelector(`mark[data-span-id="${spanCursor}"]`);
    if (node) {
      node.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      node.focus?.({ preventScroll: true });
    }
  }, [spanCursor, cursorScroll]);

  const addedRanges = useMemo(
    () => humanAddedSpans(draft.agentCaption, draft.working, draft.spans),
    [draft.agentCaption, draft.working, draft.spans],
  );

  const overlap = selection ? findOverlap(draft.spans, selection) : null;

  return (
    <div className="annotation-caption-column">
      <header className="annotation-header">
        <div>
          <h2>Orchestration Final Caption Review</h2>
          <p>
            {sample?.caption_source ? shortSource(sample.caption_source) : ''}
            {sample?.caption_model ? ` · ${sample.caption_model}` : ''}
            {sample?.human_review_flag ? ' · flagged for human review' : ''}
          </p>
        </div>
        <div className="annotation-actions">
          <button type="button" className="btn sm danger" onClick={onClear}>
            <Trash2 size={13} /> Clear
          </button>
        </div>
      </header>

      {sample?.human_review_reason && (
        <p className="final-reason">
          <strong>Review reason:</strong> {sample.human_review_reason}
        </p>
      )}

      {/* ---- agent adjudication ---- */}
      <section className="agent-adjudication-review">
        <div className="adjudication-head">
          <h3>Agent Final Adjudication</h3>
          <p>
            Review the highlighted span, confirm it, reject it, or mark that the span is valid
            but the agent action, label, or replacement is wrong. Use caption tags for actual
            text edits.
          </p>
        </div>
        <AdjudicationStats queue={queue} decisions={decisions} detailDecisions={detailDecisions} />
      </section>

      {/* ---- caption + decision ---- */}
      <section className="agent-final-caption">
        <strong>Active decision on original and final captions</strong>
        <p className="highlight-note">
          Left: original caption context. Right: the editable agent final caption for human tags
          and edits. The final saved caption below accumulates confirmed, rejected, and
          wrong-action decisions.
        </p>

        <div className="mark-buttons inline">
          {SPAN_TYPES.map((label) => (
            <button
              key={label}
              type="button"
              className={TAG_META[label].className}
              title={TAG_META[label].help}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!selection) {
                  toast('Select some caption text first', { tone: 'danger' });
                  return;
                }
                if (addTag(label) === null) {
                  toast('That selection overlaps an existing tag', { tone: 'danger' });
                }
              }}
            >
              {TAG_META[label].label} <Keycap>{TAG_META[label].hotkey}</Keycap>
            </button>
          ))}
        </div>

        <DecisionLegend />

        <DecisionQueue
          queue={queue}
          activeIndex={activeIndex}
          decisions={decisions}
          detailDecisions={detailDecisions}
          minimized={minimized}
          onMinimize={() => setMinimized((v) => !v)}
          onMove={moveQueue}
          onDecide={(verdict) => decide(verdict)}
          onNote={(note) => activeItem && setDecisionNote(slot, activeItem, note)}
        />

        <details className="disclosure">
          <summary>Agent final caption before human edits</summary>
          <p className="plain-agent-caption">{draft.agentCaption}</p>
        </details>

        {draft.remapReport && (
          <div className="remap-report" role="alert">
            <span className="grow">
              {draft.remapReport.moved > 0 &&
                `${draft.remapReport.moved} tag${draft.remapReport.moved === 1 ? '' : 's'} moved`}
              {draft.remapReport.moved > 0 && draft.remapReport.dropped.length > 0 && ' · '}
              {draft.remapReport.dropped.length > 0 &&
                `${draft.remapReport.dropped.length} dropped`}
            </span>
            <button type="button" className="btn sm" onClick={() => undoCaptionEdit(slot)}>
              <RotateCcw size={13} /> Undo edit
            </button>
            <button type="button" className="btn sm ghost" onClick={() => dismissRemapReport(slot)}>
              Dismiss
            </button>
          </div>
        )}

        <div className="active-caption-compare">
          <div className="active-caption-original">
            <h4>Original caption</h4>
            <p className="caption-surface caption-body">{sample?.original_caption || '-'}</p>
          </div>

          <div className="active-caption-final">
            <div className="row-between" style={{ marginBottom: 4 }}>
              <h4>Agent final caption</h4>
            </div>

            <CaptionSurface
              ref={surfaceRef}
              text={draft.working}
              spans={draft.spans}
              addedSpans={addedRanges}
              extraRanges={decisionRanges}
              activeSpanIndex={spanCursor}
              showTagLetters={showTagLetters}
              editable
              onCommit={(next) => {
                const report = commitCaptionEdit(slot, next);
                if (report?.dropped) {
                  toast(`${report.dropped} tag(s) could not be re-anchored`, { tone: 'danger' });
                }
              }}
              onSpanClick={(index, event) =>
                openSpanPopover(index, event.currentTarget.getBoundingClientRect())
              }
              onMouseUp={captureSelection}
              onKeyUp={(e) => e.shiftKey && captureSelection(e)}
            />
          </div>
        </div>
      </section>

      <DecisionList
        queue={queue}
        decisions={decisions}
        detailDecisions={detailDecisions}
        onJump={setActiveId}
        onDecide={(item, verdict) => decide(verdict || 'pending', item, false)}
        onNote={(item, note) => setDecisionNote(slot, item, note)}
      />

      <FinalPreview
        draft={draft}
        onChange={(text) => setFinalCaption(slot, text)}
        onResync={(text) => setFinalCaption(slot, text)}
        agentLayer={agentLayer}
      />

      <SpanGrid
        spans={draft.spans}
        cursor={spanCursor}
        onCursor={setCursor}
        onUpdate={updateSpan}
        onRetag={retagSpan}
        onRemove={(index) => {
          const removed = removeSpan(index);
          if (removed) {
            toast(`Removed the tag on "${removed.text.slice(0, 32)}"`, {
              action: { label: 'Undo', onClick: () => restoreSpan(removed) },
            });
          }
        }}
      />

      {spanPopover && draft.spans[spanPopover.index] && (
        <SpanPopover
          span={draft.spans[spanPopover.index]}
          index={spanPopover.index}
          anchor={spanPopover.anchor}
          onUpdate={updateSpan}
          onRetag={retagSpan}
          onRemove={(index) => {
            const removed = removeSpan(index);
            if (removed) {
              toast(`Removed the tag on "${removed.text.slice(0, 32)}"`, {
                action: { label: 'Undo', onClick: () => restoreSpan(removed) },
              });
            }
          }}
          onClose={closeSpanPopover}
        />
      )}

      {!spanPopover && (
        <SelectionPopover
          selection={selection}
          overlap={overlap}
          onTag={(label) => {
            if (addTag(label) === null) {
              toast('That selection overlaps an existing tag', { tone: 'danger' });
            }
          }}
          onGoToOverlap={() => {
            setCursor(draft.spans.indexOf(overlap));
            setSelection(null);
          }}
          onDismiss={() => setSelection(null)}
        />
      )}
    </div>
  );
}
